import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import * as yup from "yup";
import MCard, { MCardControlMethods, MCardField } from "components/Shared/MCard";
import { TippedIcon, MDropzone, MSpinner } from "components/Shared";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import { LENDER, INSURANCE, LENDER_APPRAISAL, LENDER_NON_APPRAISAL, COMMERCIAL } from "typeConstants/webOrder";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_MONEY,
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_DATE,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_EMAIL,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_NUMBER_STRING,
  DATA_TYPE_BOOLEAN,
} from "typeConstants/dataTypes";
import { propertyTypeOptions, transactionTypeOptions, businessTypeOptions } from "components/WebOrderV2/dropdownOptions";
import { uploadFile, removeFile } from "actions/webOrder";
import responseHandler from "responseHandler";
import { nanToNull } from "tools/validation";
import { getAllActiveUnderwritersForUser } from "requests/underwriter";
import { CUSTOMER_IS_AMC, VALIDATE_UWCODE_ON_WEB_ORDERS, AGENT_CODE_REQUIRED } from "typeConstants/customerFeatures";
import { addAttachment, removeAttachment, getLenderAgencyCodes, getInsuranceAgencyCodes } from "requests/webOrder";
import { AgentInfo } from "interfaces/Mueller.Business.Models";

const PolicyOrLoan = props => {

  const getFileList = () => {
    return props.data?.fileList ? [...props.data?.fileList] : [];
  };

  const [brokerOptions, setBrokerOptions] = useState([]);
  const [lenderOrAgentOptions, setLenderOrAgentOptions] = useState(null);
  const [fileList, setFileList] = useState(getFileList());
  const [underwriters, setUnderwriters] = useState(null);
  const [showOtherTransaction, setShowOtherTransaction] = useState(props.data.transactionType?.value === "Other");
  const [requireDesktopUnderwriterId, setRequireDesktopUnderwriterId] = useState(
    (props.handlingFeatures?.desktopUnderwriterIdRequired ?? false) || props.data?.fnmaApi);
  const [requireLoanProspectorId, setRequireLoanProspectorId] = useState(
    (props.handlingFeatures?.loanProspectorIdRequired ?? false) || props.data?.freApi);
  const [showApiRequiredValidation, setShowApiRequiredValidation] = useState(false);
  const [agentList, setAgentList] = useState<AgentInfo[]>([]);

  const mCardControlMethodsLoanInformation = useRef<MCardControlMethods>(null);
  const mCardControlMethodsLenderOrAgent = useRef<MCardControlMethods>(null);
  const mCardControlMethodsAppraisalApis = useRef<MCardControlMethods>(null);
  const mounted = useRef(true);
  const isFirstRender = useRef(true);

  let showLenderOrAgent = props.accountFeatures.features?.includes(CUSTOMER_IS_AMC) || props.pageLayout === INSURANCE || props.pageLayout === COMMERCIAL;

  useEffect(() => {
    if(props.pageLayout === INSURANCE || props.pageLayout === COMMERCIAL) {
      getInsuranceAgencyCodes(props.data?.customer?.value, (agents) => setAgentList(agents), mounted);
    }
    else {
      getLenderAgencyCodes(props.data?.customer?.value, (agents) => setAgentList(agents), mounted);
    }
    
    if (props.terminology === INSURANCE && props.features?.includes(VALIDATE_UWCODE_ON_WEB_ORDERS)) {
      getAllActiveUnderwritersForUser(payload => setUnderwriters(payload), mounted);
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (agentList !== null) {      
      if (showLenderOrAgent) {
        handleLendersOrAgents(agentList);
      }
      else {
        handleBrokers(agentList);
      }
    }
  }, [agentList]);

  useEffect(() => {
    mCardControlMethodsLoanInformation.current.refreshEditData();
  }, [showOtherTransaction]);

  useEffect(() => {
    if (!isFirstRender.current) {
      validateApiFields();
      mCardControlMethodsLoanInformation.current.refreshEditData();
    } else {
      isFirstRender.current = false;
    }
  }, [requireDesktopUnderwriterId, requireLoanProspectorId]);

  const setControlMethodsLoanInformation = () => {
    return cm => (mCardControlMethodsLoanInformation.current = cm);
  };

  const setControlMethodsLenderOrAgent = () => {
    return cm => (mCardControlMethodsLenderOrAgent.current = cm);
  };

  const setControlMethodsAppraisalApis = () => {
    return cm => (mCardControlMethodsAppraisalApis.current = cm);
  };

  const handleBrokers = brokers => {
    setBrokerOptions(brokers.map(b => ({ value: b.code, label: b.code })));
    if (brokers.length === 1) {
      mCardControlMethodsLoanInformation.current.setEditData("brokerId", { value: brokers[0].code, label: brokers[0].code });
    }
  };

  const getDropdownOptionFromAgent = agent => ({ value: agent.code, label: agent.name ? `${agent.name} (${agent.code})` : agent.code });

  const handleLendersOrAgents = options => {
    setLenderOrAgentOptions(options.filter(o => o).map(getDropdownOptionFromAgent));
  };

  const onPrevious = () => {
    props.onPrevious(getDataWithDropdownOptions());
  };

  const onNext = () => {
    let isValid = mCardControlMethodsLoanInformation.current.runValidation(true);
    if (showLenderOrAgent) {
      // need isValid at the end of this if statement otherwise the validations dont run for the lender or agent card
      // if the loan validations pass, because of the && short circuiting
      isValid = mCardControlMethodsLenderOrAgent.current.runValidation(true) && isValid;
    }
    if (!validateApiFields()) {
      isValid = false;
    }
    if (isValid) {
      props.onNext(getDataWithDropdownOptions());
    }
  };

  const onAgentCodeChanged = (option) => {
    let agentCode = option?.value;
    const updateLenderOrAgentData = (key, value) => {
      props.data[key] = value;
      mCardControlMethodsLenderOrAgent.current.setEditData(key, value);
    };
    let agent = agentList.filter(x => x.code === agentCode)[0];
    updateLenderOrAgentData("agentName", agent?.name);
    updateLenderOrAgentData("agentPhone", agent?.phone);
    updateLenderOrAgentData("agentEmail",  agent?.email);
    updateLenderOrAgentData("agentStreetAddress1", agent?.mailingAddress?.address1);
    updateLenderOrAgentData("agentCity", agent?.mailingAddress?.city);
    updateLenderOrAgentData("agentState", agent?.mailingAddress?.state);
    updateLenderOrAgentData("agentZipCode", agent?.mailingAddress?.zipCode);
  };

  const onTransactionTypeChanged = option => setShowOtherTransaction(option.value === "Other");
  const onFreApiChanged = value => {
    setRequireLoanProspectorId(value);
  };
  const onFnmaApiChanged = value => {
    setRequireDesktopUnderwriterId(value);
  };

  // Validating these outside of MCard as yup.bool().test() doesn't seem to work
  const validateApiFields = () => {
    if (props.handlingFeatures?.showAppraisalApiChoice && !requireDesktopUnderwriterId && !requireLoanProspectorId) {
      setShowApiRequiredValidation(true);
      return false;
    } else if (showApiRequiredValidation && requireDesktopUnderwriterId || requireLoanProspectorId) {
      setShowApiRequiredValidation(false);
    }
    return true;
  };

  const getDataWithDropdownOptions = () => {
    let loanData = mCardControlMethodsLoanInformation.current.getEditedDataObject();
    let lenderData = showLenderOrAgent ? mCardControlMethodsLenderOrAgent.current.getEditedDataObject() : {};
    let appraisalApiData = props.handlingFeatures?.showAppraisalApiChoice 
      ? mCardControlMethodsAppraisalApis.current.getEditedDataObject() : {};
    let editedData: Record<string, any> = { ...loanData, ...lenderData, ...appraisalApiData };
    // If there's only 1 broker, automatically make them selected
    if (agentList.length === 1) {
      editedData.brokerId = brokerOptions[0] ;
    } else if (editedData.brokerId) {
      editedData.brokerId = brokerOptions.filter(b => b.value === editedData.brokerId)[0];
    }
    if (editedData.propertyType) {
      editedData.propertyType = propertyTypeOptions.filter(p => p.value === editedData.propertyType)[0];
    }
    if (editedData.transactionType) {
      editedData.transactionType = transactionTypeOptions.filter(t => t.value === editedData.transactionType)[0];
    }
    if (editedData.businessType) {
      editedData.businessType = businessTypeOptions.filter(b => b.value === editedData.businessType)[0];
    }
    if (editedData.underwriterCode && underwriters) {
      editedData.underwriterCode = underwriters.filter(u => u.value === editedData.underwriterCode)[0];
    }
    editedData.fileList = fileList;

    if (props.agentCode) {
      editedData.agentCode = lenderOrAgentOptions?.filter(x => x.code == editedData.agentCode)[0];
    }

    return editedData;
  };

  const insuranceFields = [
    {
      key: "policyNumber",
      label: "Policy Number",
      value: props.data.policyNumber,
      validation: yup.string().max(50, "Policy Number may not exceed 50 characters").required("Policy Number is required"),
      placeholder: "Required",
    },
    {
      key: "trackingNumber",
      label: "Tracking ID",
      validation: yup.string().max(512, "Tracking ID cannot exceed 512 characters"),
      value: props.data.trackingNumber,
    },
    {
      key: "coverageIn",
      dataType: DATA_TYPE_MONEY,
      label: "Current Coverage",
      value: props.data.coverageIn,
      validation: yup.number().min(1, "Value must be greater than zero").max(99999999999, "Current Coverage cannot exceed 11 digits").transform(nanToNull).nullable()
    },
    {
      key: "yearBuilt",
      dataType: DATA_TYPE_NUMBER_UNFORMATTED,
      label: "Year Built",
      value: props.data.yearBuilt,
      validation: yup.number().integer("Value must be a whole number").min(1701, "Value must be greater than 1700").max(new Date().getFullYear(), "Value must not be more than the current year").transform(nanToNull).nullable()
    },
    {
      key: "sqFt",
      dataType: DATA_TYPE_NUMBER,
      label: "Square Ft.",
      value: props.data.sqFt,
      validation: yup.number().integer("Value must be a whole number").min(1, "Value must be greater than zero").max(99999, "Value must be less than 100,000").transform(nanToNull).nullable()
    },
    {
      key: "effectiveDate",
      dataType: DATA_TYPE_DATE,
      label: "Effective Date",
      value: props.data.effectiveDate,
    },
    {
      key: "businessType",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Business Type",
      value: props.data.businessType,
      dataTypeOptions: {
        options: businessTypeOptions
      },
    },
    {
      key: "orderCodeIn",
      label: "Order Code In",
      value: props.data.orderCodeIn,
      validation: yup.string().max(50, "Order code in cannot be more than 50 characters.")
    },
    {
      key: "startDate",
      dataType: DATA_TYPE_DATE,
      label: "Start Date",
      value: props.data.startDate,
    },
  ];

  // remove fields handled in previous section for commercial surveys
  if (props.pageLayout === COMMERCIAL) {
    insuranceFields.splice(insuranceFields.findIndex(f => f.key === "trackingNumber"), 1);
    insuranceFields.splice(insuranceFields.findIndex(f => f.key === "yearBuilt"), 1);
    insuranceFields.splice(insuranceFields.findIndex(f => f.key === "sqFt"), 1);
  }

  const underwriterTextField = [
    {
      key: "underwriterCode",
      label: "UW code",
      value: props.data.underwriterCode,
      validation: yup.string().max(50, "UW code cannot be more than 50 characters.")
    },
  ];

  const underwriterDropdownField = [
    {
      key: "underwriterCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "UW code",
      value: props.data.underwriterCode,
      dataTypeOptions: { options: underwriters }
    },
  ];

  const standardLenderFields = [
    {
      key: "policyNumber",
      label: <>Loan Number <TippedIcon
      iconName="info"
      tooltipText={"If this order is not for a loan and you do not have a loan number, please enter the owner’s last name."}
      iconClassName={"no-opt-in-edits"}
      tooltipPopoutDirection="bottom"
    /></>,
      value: props.data.policyNumber,
      validation: yup.string().max(50, "Loan Number may not exceed 50 characters").required("Loan Number is required"),
      placeholder: "Required",
    },
    {
      key: "propertyType",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Property Type",
      value: props.data.propertyType,
      dataTypeOptions: { options: propertyTypeOptions },
      validation: yup.string().required("Property Type is required"),
      placeholder: "Select... (required)",
    },
    {
      key: "transactionType",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Transaction Type",
      value: props.data.transactionType,
      dataTypeOptions: { options: transactionTypeOptions },
      onChange: onTransactionTypeChanged,
      validation: yup.string().required("Transaction Type is required"),
      placeholder: "Select... (required)",
    },
  ];

  const otherTransactionField = [
    {
      key: "otherTransactionType",
      label: "Other type",
      value: props.data.otherTransactionType,
      validation: yup.string().required("Required"),
      placeholder: "Required",
    },
  ];

  const desktopUnderwriterIdField = [
    {
      key: "desktopUnderwriterId",
      label: "Desktop Underwriter Id (FNMA)",
      dataType: DATA_TYPE_NUMBER_UNFORMATTED,
      value: props.data.desktopUnderwriterId,
      validation: requireDesktopUnderwriterId
        ? yup.number().max(9999999999, "Desktop Underwriter Id may not exceed 10 characters").required("Desktop Underwriter Id is required")
        : yup.number().max(9999999999, "Desktop Underwriter Id may not exceed 10 characters"),
      placeholder: requireDesktopUnderwriterId ? "Required" : null,
    }
  ];

  const loanProspectorIdField = [
    {
      key: "loanProspectorId",
      label: <>Loan Prospector Key Number (FHLMC) <TippedIcon
        iconName="info"
        tooltipText={"The LP ID should be 8 characters and start with a letter (e.g., A1234567). The LP ID is also known as Loan Prospector® (LP) AUS Key Number & LP Key Number."}
        iconClassName={"no-opt-in-edits"}
        tooltipPopoutDirection="bottom"/>
      </>,      
      value: props.data.loanProspectorId,
      validation: requireLoanProspectorId
        ? yup.string().max(8, "Loan Prospector Key Number may not exceed 8 characters").required("Loan Prospector Key Number is required")
        : null,
      placeholder: requireLoanProspectorId ? "Required" : null,
    },
  ];

  const brokerIdField = [
    {
      key: "brokerId",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Broker Id",
      value: props.data.brokerId,
      dataTypeOptions: { options: brokerOptions }
    },
  ];

  let agentCodeRequired = props.accountFeatures.features?.includes(AGENT_CODE_REQUIRED);

  let nameString = props.terminology === INSURANCE ? "Agency Name" : "Name";

  const lenderOrAgentFields = [
    {
      key: "agentCode",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "Code",
      value: lenderOrAgentOptions?.find(ddOption => ddOption.value === props.data.agentCode),
      dataTypeOptions: { options: lenderOrAgentOptions, creatable: true },
      onChange: onAgentCodeChanged,
      validation: agentCodeRequired ? yup.string().required("Agent Code is required") : null,
      placeholder: agentCodeRequired ? "Select... (required)" : null,
    },
    {
      key: "agentName",
      label: nameString,
      validation: yup.string().nullable().max(50, `${nameString} may not exceed 50 characters`),
      value: props.data.agentName,
    },
    {
      key: "agentStreetAddress1",
      dataType: DATA_TYPE_ADDRESS,
      label: "Address",
      validation: yup.string().nullable().max(75, "Address cannot exceed 75 characters"),
      value: props.data.agentStreetAddress1,

    },
    {
      key: "agentCity",
      label: "City",
      validation: yup.string().nullable().max(50, "City cannot exceed 50 characters"),
      value: props.data.agentCity,
    },
    {
      key: "agentState",
      dataType: DATA_TYPE_DROPDOWN2,
      label: "State",
      value: props.data.agentState,
      dataTypeOptions: { options: props.stateOptions },
    },
    {
      key: "agentZipCode",
      dataType: DATA_TYPE_NUMBER_STRING,
      label: "Zip",
      value: props.data.agentZipCode,
      validation: yup.string().nullable().min(5, "Value must be 5 digits").max(5, "Value must be 5 digits"),
    },
    {
      key: "agentPhone",
      dataType: DATA_TYPE_PHONE_NUMBER,
      label: "Phone",
      value: props.data.agentPhone,
    },
    {
      key: "agentEmail",
      dataType: DATA_TYPE_EMAIL,
      label: "Email",
      value: props.data.agentEmail,
      validation: yup.string().nullable().max(100, "Email cannot exceed 100 characters").email("Email must be valid")
    },
  ];

  const appraisalApiFields: MCardField[] = [
    {
      key: "freApi",
      dataType: DATA_TYPE_BOOLEAN,
      label: "ACE+ PDR (FHLMC)",
      value: props.data.freApi,
      onChange: onFreApiChanged,
    },
    {
      key: "fnmaApi",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Value Acceptance + Property Data (FNMA)",
      value: props.data.fnmaApi,
      onChange: onFnmaApiChanged,
    },
  ];

  const getFormFields = () => {
    const fields = [];
    if (props.pageLayout === INSURANCE || props.pageLayout === COMMERCIAL) {
      fields.push(...insuranceFields);
      if (underwriters) {
        fields.push(...underwriterDropdownField);
      } else {
        fields.push(...underwriterTextField);
      }
    }
    if (props.pageLayout === LENDER_APPRAISAL || props.pageLayout === LENDER_NON_APPRAISAL) {
      fields.push(...standardLenderFields);
      if (showOtherTransaction) {
        fields.push(...otherTransactionField);
      }
      
      if (!showLenderOrAgent) {
        fields.push(...brokerIdField);
      }
    }
    // There are scenarios where we need to show both, otherwise we should only show when required
    if (props.handlingFeatures?.showAppraisalIdFields || (requireLoanProspectorId && requireDesktopUnderwriterId)) {
      fields.push(...loanProspectorIdField, ...desktopUnderwriterIdField);
    } else {
      if (requireDesktopUnderwriterId) {
        fields.push(...desktopUnderwriterIdField);
      }
      if (requireLoanProspectorId) {
        fields.push(...loanProspectorIdField);
      }
    }
    return fields;
  };


  const getLenderOrAgent = (terminology) => {
    if (!lenderOrAgentOptions) {
      return <MSpinner />;
    } else {
      return (
        <MCard
          title={terminology === INSURANCE ? "Agent Information" : "Lender Information"}
          getControlMethods={setControlMethodsLenderOrAgent()}
          fields={lenderOrAgentFields}
          onlyEditable
        />
      );
    }
  };

  const getAppraisalApiCard = () => {
    let fields = appraisalApiFields;
    if (showApiRequiredValidation) {
      fields.push( {
        key: "apiSelectionRequired",
        label: "Please choose 1 or more inspection types",
        isLabelOnly: true,
        className: "card-error",
      });
    }
    return (
      <MCard
      title="GSE Inspection Type"
      getControlMethods={setControlMethodsAppraisalApis()}
      fields={fields}
      onlyEditable
    />
    );
  };

  let cardTitle = props.terminology === LENDER ? "Loan" : "Policy";

  
  const dropzoneRightsOverrides = {
    download: false,
    preview: false,
    delete: true,
  };

  const onFileAdd = (files) => {
    const filteredFiles = files.filter(({ file }) => file);
    if (filteredFiles.length)
      filteredFiles.map(({ file }) => {
        addAttachment(
          file,
          (response) => {
            fileList.push({
              id: response,
              fileName: file.path,
              lastModified: file.lastModified,
              fileSizeBytes: file.size,
              canDelete: true,
            });
          },
          () => props.makeToast("Upload failed. Please try again.", "error"),
          mounted
        );
      });
  };

  const onFileRemove = (files) => {
    if (files.length) {
      files.forEach(file => {
        const attached = fileList.find(a => a.fileName === file.name);
        if (attached) {
          removeAttachment(
            attached.id,
            () => setFileList(fileList.filter(a => a.id !== attached.id)),
            mounted
          );
        }
      });
    }
  };
  
  const getFileUploadCard = (terminology) => {
    return (
      <MCard title={terminology === LENDER ? "Upload Files" : "Upload DEC Files"}>
        <div className="manual-order-files-and-buttons">
          <div className="manual-order-dropzone file-manager">
            {/* @ts-ignore not sure why it thinks it knows the types for MDropzone */}
            <MDropzone
              multiple
              noClick
              onFileAdd={onFileAdd}
              onFileRemove={onFileRemove}
              prompt={terminology === LENDER ? "Drop files here to upload" : "Drop DEC files here to upload"}
              fileRightOverrides={dropzoneRightsOverrides}
              trackFiles
              existingFiles={fileList ? fileList : []}
              columnDefinitionOverride={[
                { name: "Name", dataSource: "name", className: "file-name-cell" },
                { name: "Type", dataSource: "extension", style: { width: "50px" } },
                { name: "Actions", dataSource: "actions", style: { width: "160px" } },
              ]}
            />
          </div>
        </div>
      </MCard>
    );
  };

  return (
    <>
      {props.handlingFeatures?.showAppraisalApiChoice && getAppraisalApiCard()} 

      <MCard
        title={cardTitle}
        getControlMethods={setControlMethodsLoanInformation()}
        fields={getFormFields()}
        onlyEditable
      />

      {showLenderOrAgent && getLenderOrAgent(props.terminology)}

      {getFileUploadCard(props.terminology)}
        <OrderStepButtons
          currentStep={props.currentStep}
          onPrevious={onPrevious}
          onNext={onNext}
        />
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  uploadFile(file, fileId, uploadFinishedCallback) {
    dispatch(uploadFile(file, fileId, uploadFinishedCallback));
  },
  removeFile(fileId) {
    dispatch(removeFile(fileId));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});

export default connect(null, mapDispatchToProps)(PolicyOrLoan);

PolicyOrLoan.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, Insurance, Commercial: see typeConstants/webOrder.js
  handlingFeatures: PropTypes.object,  // caseHandlingFeatures from settings
  stateOptions: PropTypes.array,  // State abbrevation options for address field

  // Redux
  uploadFile: PropTypes.func,
  removeFile: PropTypes.func,
  makeToast: PropTypes.func,
};